import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, MenuItem, Divider, Chip, Autocomplete, Checkbox } from "@mui/material";
import { GroupHeader, GroupItems } from "../../../utils/Styled";
import { Countries } from "../../../utils/Lookup";
import { Icons } from "../../../utils/Icon";
import _ from "lodash";
import axios from "axios";
import Swal from "sweetalert2";

export default function CreateProductGroupForm({ open, handleClose, fetchData, userCountry, defaultCountryId }) {
    const [newData, setNewData] = useState({});
    const [productsData, setProductsData] = useState([]);
    const [errors, setErrors] = useState({ group_name: false, group_description: false, country_id: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "group_name" && value === "") {
            await setError("group_name", "Name is required.");
        } else if (field === "group_description" && value === "") {
            await setError("group_description", "Description is required.");
        } else if (field === "country_id" && value === "") {
            await setError("country_id", "Manage By is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    useEffect(() => {
        if (open) {
            const selectedDefaultCountry = _.findIndex(userCountry, { country_id: defaultCountryId }) < 0 ? "" : defaultCountryId;
            setNewData({
                group_name: "",
                group_description: "",
                country_id: selectedDefaultCountry,
                product_id: [],
            });
            setProductsData([]);
            setErrors({ group_name: false, group_description: false, country_id: false });
            setHelperText({});
            setErrorMessage("");
        }
    }, [open, userCountry, defaultCountryId]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (!newData.country_id) {
            setProductsData([]);
            return;
        }

        axios
            .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/select?country=${newData.country_id}&is_contract_product=1`)
            .then((response) => setProductsData(response.data.data.products))
            .catch((error) => console.log(error));
    }, [open, newData.country_id]);

    const handleCountryChange = async (countryId) => {
        const hasSelectedProducts = (newData.product_id || []).length > 0;
        const isSameCountry = String(newData.country_id || "") === String(countryId || "");

        if (hasSelectedProducts && !isSameCountry) {
            const confirmReset = await Swal.fire({
                title: "Change Manage By?",
                text: "Changing Manage By will reset selected products.",
                icon: "warning",
                allowOutsideClick: false,
                showCancelButton: true,
                confirmButtonColor: "#fe7320",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, continue",
            });

            if (!confirmReset.isConfirmed && !confirmReset.value) {
                return;
            }
        }

        setNewData({ ...newData, country_id: countryId, product_id: [] });
        validate("country_id", countryId);
    };

    const handleCreate = async () => {
        let isValid = true;

        if (newData.group_name === "") isValid = false;
        if (newData.group_description === "") isValid = false;
        if (newData.country_id === "") isValid = false;

        Object.keys(newData).forEach(async (field) => {
            await validate(field, newData[field]);
        });

        if (!isValid) {
            await setErrorMessage("Please fill all the required fields.");
            return;
        }

        try {
            const { group_name, group_description, country_id, product_id } = newData;
            const selected_products = (product_id || []).map((product) => product.product_id);
            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/product-groups/create", {
                group_name,
                group_description,
                country_id,
                selected_products,
            });

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Product group created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the product group. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Product Group</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    id="group_name"
                    label="Name"
                    type="text"
                    fullWidth
                    required
                    value={newData.group_name || ""}
                    error={errors.group_name}
                    helperText={helperText.group_name}
                    onChange={(e) => {
                        setNewData({ ...newData, group_name: e.target.value });
                        validate("group_name", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="group_description"
                    label="Description"
                    type="text"
                    multiline
                    rows={3}
                    fullWidth
                    required
                    value={newData.group_description || ""}
                    error={errors.group_description}
                    helperText={helperText.group_description}
                    onChange={(e) => {
                        setNewData({ ...newData, group_description: e.target.value });
                        validate("group_description", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="country_id"
                    select
                    label="Manage By"
                    type="number"
                    fullWidth
                    required
                    value={newData.country_id || ""}
                    error={errors.country_id}
                    helperText={helperText.country_id}
                    onChange={(e) => {
                        handleCountryChange(e.target.value);
                    }}
                >
                    {userCountry.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id}>
                            {country.country_name}
                        </MenuItem>
                    ))}
                </TextField>
                <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Contract Product Assignment" />
                </Divider>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={productsData}
                    groupBy={(option) => {
                        const country = Countries.find((country) => country.id === option.country_id);
                        return country ? country.name : "Unknown";
                    }}
                    getOptionLabel={(option) => option.product_description}
                    value={newData.product_id || []}
                    onChange={(event, value) => {
                        setNewData({
                            ...newData,
                            product_id: value || [],
                        });
                    }}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={Icons.CheckBoxBlankIcon} checkedIcon={Icons.CheckBoxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                            {option.product_description}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth error={errors.product_id} helperText={helperText.product_id} />}
                    renderGroup={(params) => {
                        return (
                            <li key={params.key}>
                                <GroupHeader>{params.group}</GroupHeader>
                                <GroupItems>{params.children}</GroupItems>
                            </li>
                        );
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreate} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Create
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
